// Mobile Game Launcher Utility
// Handles mobile orientation detection and fullscreen before launching games

export const launchGameWithMobileOptimization = (gameUrl) => {
  // Check if device is mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                   (window.innerWidth <= 768 && window.innerHeight <= 1024);

  if (!isMobile) {
    // Desktop - just open the game normally
    window.open(gameUrl, '_blank');
    return;
  }

  // Mobile device detected
  console.log('Mobile device detected, optimizing for mobile gaming');

  // Check if device supports orientation lock
  const supportsOrientationLock = 'orientation' in window.screen && 'lock' in window.screen.orientation;
  
  // Check if device supports fullscreen
  const supportsFullscreen = document.fullscreenEnabled || 
                             document.webkitFullscreenEnabled || 
                             document.mozFullScreenEnabled || 
                             document.msFullscreenEnabled;

  // Function to enter fullscreen
  const enterFullscreen = () => {
    if (!supportsFullscreen) return;
    
    const elem = document.documentElement;
    
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  };

  // Function to force landscape orientation
  const forceLandscape = () => {
    if (supportsOrientationLock) {
      window.screen.orientation.lock('landscape').catch(function(error) {
        console.log('Orientation lock failed:', error);
      });
    }
  };

  // Show mobile optimization message
  const showMobileMessage = () => {
    const message = document.createElement('div');
    message.id = 'mobile-game-launch-message';
    message.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: 
        radial-gradient(900px 500px at 0% -10%, rgba(91,108,255,0.15), transparent 50%),
        radial-gradient(900px 500px at 110% 10%, rgba(157,140,255,0.15), transparent 45%),
        linear-gradient(180deg, #0b0d10, #0a0c10);
      color: #e6e9ef;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      text-align: center;
      padding: 20px;
      backdrop-filter: blur(20px);
      border: 1px solid #1e2430;
    `;

    message.innerHTML = `
      <div style="
        font-size: 24px; 
        margin-bottom: 16px; 
        font-weight: 700;
        background: linear-gradient(135deg, #5b6cff, #9d8cff);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      ">Optimizing for Mobile Gaming</div>
      <div style="
        font-size: 16px; 
        margin-bottom: 20px; 
        color: #a5adba;
        max-width: 300px;
        line-height: 1.5;
      ">
        ${supportsOrientationLock ? 'Locking to landscape mode...' : 'Please rotate to landscape mode'}
      </div>
      <div style="
        font-size: 14px; 
        color: #7f85d6; 
        margin-bottom: 32px;
        max-width: 300px;
        line-height: 1.5;
      ">
        ${supportsFullscreen ? 'Entering fullscreen mode...' : 'Fullscreen not supported on this device'}
      </div>
      <button id="launch-game-btn" style="
        background: linear-gradient(135deg, #5b6cff, #9d8cff);
        color: #ffffff;
        border: none;
        padding: 16px 32px;
        border-radius: 999px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        font-family: 'Inter', sans-serif;
        letter-spacing: 0.2px;
        box-shadow: 0 10px 30px rgba(91,108,255,0.35);
        position: relative;
        overflow: hidden;
      ">Launch Game Now</button>
    `;

    document.body.appendChild(message);

    // Add button event listener
    const launchBtn = document.getElementById('launch-game-btn');
    
    // Add hover effects
    launchBtn.addEventListener('mouseenter', () => {
      launchBtn.style.transform = 'translateY(-2px)';
      launchBtn.style.boxShadow = '0 15px 40px rgba(91,108,255,0.5)';
    });
    
    launchBtn.addEventListener('mouseleave', () => {
      launchBtn.style.transform = 'translateY(0)';
      launchBtn.style.boxShadow = '0 10px 30px rgba(91,108,255,0.35)';
    });
    
    launchBtn.addEventListener('click', () => {
      message.remove();
      window.open(gameUrl, '_blank');
    });

    // Auto-launch after 3 seconds
    setTimeout(() => {
      if (message.parentNode) {
        message.remove();
        window.open(gameUrl, '_blank');
      }
    }, 3000);
  };

  // Initialize mobile optimization
  const initMobileOptimization = () => {
    // Force landscape orientation
    forceLandscape();
    
    // Enter fullscreen
    enterFullscreen();
    
    // Show message
    showMobileMessage();
  };

  // Start mobile optimization
  initMobileOptimization();
};
